import React, {FunctionComponent, useEffect, useState} from 'react';
import Styles from 'views/dateAndTime/DateAndTime.module.scss';
import SwitchButton from 'components/button/switchButton/SwitchButton';
import SettingsActionButtons from 'components/settingsActionButtons';
import arrowRight from 'assets/svg/right arrow_blue.svg';
import TimeFormat from 'components/timeFormat/TimeFormat';
import DateFormat from 'components/dateFormat/DateFormat';
import CustomModal from 'components/customModal/CustomModal';
import ModalShell from 'components/chatModal/ChatModalShell.module.scss';
import Toast from 'components/toast/Tost';
import { DEFAULT_DATE_FORMAT, DEFAULT_TIME_FORMAT } from 'helpers/dateTimeFormat';

interface DateAndTimeProps {
  getSettingRequest: Function;
  updateSetting: Function;
  saving: boolean;
  setting: boolean;
  settingSuccess: string;
  settingError: string;
}
const DateAndTime: FunctionComponent<DateAndTimeProps> = (props: DateAndTimeProps) => {
  const { getSettingRequest, updateSetting, saving, setting, settingSuccess, settingError } = props;
  // @ts-ignore
  const { is_default, date_format, time_format } = setting;
  const [isDefault, setIsDefault] = useState<boolean>(is_default || false);
  const [date, setDate] = useState<string>(date_format || DEFAULT_DATE_FORMAT);
  const [time, setTime] = useState<string>(time_format || DEFAULT_TIME_FORMAT);
  const [show, setShow] = useState<boolean>(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [toast, setToast] = useState(false);
  const [toastValues, setToastValues] = useState({message: '', type: ''});
  const [saveAttempted, setSaveAttempted] = useState(false);
  const handleCancel = () => {
    if (!setting || typeof setting !== 'object') {
      setIsDefault(false);
      setDate(DEFAULT_DATE_FORMAT);
      setTime(DEFAULT_TIME_FORMAT);
      return;
    }
    setIsDefault(Boolean(is_default));
    setDate(date_format || DEFAULT_DATE_FORMAT);
    setTime(time_format || DEFAULT_TIME_FORMAT);
  };
  const handleChanges = () => {
    setShow(true);
    setSaveAttempted(true);
    updateSetting({
      date_format: date,
      time_format: time,
      is_default: isDefault,
    });
  };
  const closeDialog = () => {
    setShowTimeModal(false);
    setShowDateModal(false);
  };

  const handleDateValue = (e: string) => {
    setShowDateModal(false);
    if (e) {
      setDate(e);
    }
  };

  const handleTimeValue = (e: string) => {
    setShowTimeModal(false);
    if (e) {
      setTime(e);
    }
  };

  const handleDefault = () => {
    const next = !isDefault;
    setIsDefault(next);
    if (next) {
      setShowDateModal(false);
      setShowTimeModal(false);
    }
  };

  const openDateModal = () => {
    if (isDefault) return;
    setShowDateModal(true);
  };

  const openTimeModal = () => {
    if (isDefault) return;
    setShowTimeModal(true);
  };

  useEffect(() => {
    getSettingRequest({});
  }, []);

  useEffect(() => {
    if (!setting || typeof setting !== 'object') return;

    setIsDefault(Boolean(is_default));
    setDate(date_format || DEFAULT_DATE_FORMAT);
    setTime(time_format || DEFAULT_TIME_FORMAT);
  }, [setting]);

  useEffect(() => {
    if (!settingSuccess || !saveAttempted) return;
    setToast(true);
    setToastValues({
      message: 'Your changes have been saved.',
      type: 'Success',
    });
    setSaveAttempted(false);
    const timer = setTimeout(() => {
      setShow(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [settingSuccess, saveAttempted]);

  useEffect(() => {
    if (!settingError || !saveAttempted) return;
    setToast(true);
    setToastValues({
      message: 'Could not save date and time settings. Please try again.',
      type: 'Error',
    });
    setShow(false);
    setSaveAttempted(false);
  }, [settingError, saveAttempted]);

  return (
    <>
      <div className={Styles.settingsContent}>
        <div className={Styles.pageCard}>
          <div className="d-flex justify-content-between">
            <div className={Styles.rowText}>
              <p className={Styles.rowTitle}>Automatic date and time</p>
              <span className={Styles.rowSubtitle}>Use network-provided date and time</span>
            </div>
            <SwitchButton isChecked={isDefault} handleChange={handleDefault} />
          </div>
          <div
            className={`d-flex justify-content-between mt-60 ${Styles.formatRow}${
              isDefault ? ` ${Styles.formatRowDisabled}` : ''
            }`}
            onClick={openDateModal}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openDateModal();
              }
            }}
            role="button"
            tabIndex={isDefault ? -1 : 0}
            aria-disabled={isDefault}
          >
            <div className={Styles.rowText}>
              <p className={Styles.rowTitle}>Date Format</p>
              <span className={Styles.rowSubtitle}>
                {isDefault ? DEFAULT_DATE_FORMAT : date}
              </span>
            </div>
            <img src={arrowRight} alt="" className={Styles.iconWidth} />
          </div>
          <div
            className={`d-flex justify-content-between mt-60 ${Styles.formatRow}${
              isDefault ? ` ${Styles.formatRowDisabled}` : ''
            }`}
            onClick={openTimeModal}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openTimeModal();
              }
            }}
            role="button"
            tabIndex={isDefault ? -1 : 0}
            aria-disabled={isDefault}
          >
            <div className={Styles.rowText}>
              <p className={Styles.rowTitle}>Time Format</p>
              <span className={Styles.rowSubtitle}>
                {isDefault ? DEFAULT_TIME_FORMAT : time}
              </span>
            </div>
            <img src={arrowRight} alt="" className={Styles.iconWidth} />
          </div>
        </div>
      </div>
      <SettingsActionButtons
        onCancel={handleCancel}
        onSave={handleChanges}
        saving={saving}
      />
      {showDateModal && (
        <CustomModal
          closeDialog={closeDialog}
          title="Date Format"
          wrapperClass={`${ModalShell.wrapper} ${Styles.wrapperModal}`}
          modalHeaderClass={ModalShell.header}
          modalTitleClass={Styles.formatModalTitle}
          cssClass={ModalShell.body}
        >
          <DateFormat
            selectedValue={isDefault ? DEFAULT_DATE_FORMAT : date}
            handleClose={(e: string) => handleDateValue(e)}
          />
        </CustomModal>
      )}
      {showTimeModal && (
        <CustomModal
          closeDialog={closeDialog}
          title="Time Format"
          wrapperClass={`${ModalShell.wrapper} ${Styles.wrapperModal}`}
          modalHeaderClass={ModalShell.header}
          modalTitleClass={Styles.formatModalTitle}
          cssClass={ModalShell.body}
        >
          <TimeFormat
            selectedValue={isDefault ? DEFAULT_TIME_FORMAT : time}
            handleClose={(e: string) => handleTimeValue(e)}
          />
        </CustomModal>
      )}
      {toast && (
        <Toast
          message={toastValues.message}
          position="topRight"
          type={toastValues.type}
          handleCloseToast={setToast}
        />
      )}

    </>
  );
};

export default DateAndTime;
